import { pickFiles } from '../utils/UNSAFE_filePickerUtils';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { usePress } from '../hooks/UNSAFE_usePress';
import { ComponentMessageItem } from '../UNSAFE_ComponentMessage';
import { BundleType } from '../resources/nls/bundle';

import { FilePicker, CommitDetail, RejectDetail } from './FilePicker';
import {
  validateTypes,
  getMimeTypeValidationMessages,
  createFileList,
  validateSelectionMode
} from './FilePickerUtils';
import { fireRejectAction, handleFilesAdded } from './postValidationUtils';

import { ComponentProps } from 'preact';
import { useRef, Ref, StateUpdater } from 'preact/hooks';

type FilePickerProps = ComponentProps<typeof FilePicker>;

type FilePickerElementProps = FilePickerProps & {
  translations: BundleType;
};

/**
 * The file picker helper method for non-disabled filepickers.
 */
export const useFilepickerElementHelper = (
  props: FilePickerElementProps,
  rootRef: Ref<HTMLDivElement>,
  setValidity?: StateUpdater<'NA' | 'valid' | 'invalid'>
) => {
  const { callback, validation } = clickCallbackUtils(props);
  const { pressProps } = usePress(callback);

  const dragAndDropProps = useDragAndDropEvents(
    props.accept,
    props.selectionMode,
    fireRejectAction,
    handleFilesAdded,
    rootRef,
    props.translations,
    props.onCommit,
    props.onReject,
    setValidity
  );
  return {
    validation,
    dragAndDropProps,
    pressProps
  };
};

type ClickProps = {
  accept?: FilePickerProps['accept'];
  capture?: FilePickerProps['capture'];
  selectionMode?: FilePickerProps['selectionMode'];
  onCommit?: FilePickerProps['onCommit'];
  onReject?: FilePickerProps['onReject'];
  translations: BundleType;
};

const clickCallbackUtils = (props: ClickProps) => {
  /**
   * Callback function when the filepicker is clicked.
   */
  const handleSelectingFiles = (event: Event) => {
    // Bug 27775173 - within firefox browser cannot tab past filepicker without file selector
    // window opening
    // only launching the file picker if click or 'Enter' was pressed
    if (
      event.type === 'click' ||
      (event.type === 'keyup' && (event as KeyboardEvent).code === 'Enter')
    ) {
      // Bug 26907138 - form submit event is triggered when the file upload button is clicked
      event.preventDefault();

      pickFiles(_handleClickValidation, {
        accept: props.accept,
        selectionMode: props.selectionMode ?? 'multiple',
        capture: props.capture ?? 'none'
      });
    }
  };

  /**
   * Handles file validation from selecting files through the file dialog
   */
  const _handleClickValidation = (files: FileList) => {
    if (files.length > 0) {
      const rejected = validateTypes(files, props.accept, props.translations).rejected;
      if (rejected.length > 0) {
        fireRejectAction(
          getMimeTypeValidationMessages(rejected, props.translations),
          false,
          props.onReject
        );
      } else {
        handleFilesAdded(files, props.onCommit);
      }
    }
  };
  return {
    callback: handleSelectingFiles,
    validation: _handleClickValidation
  };
};

const useDragAndDropEvents = (
  accept: FilePickerProps['accept'],
  selectionMode: FilePickerProps['selectionMode'],
  fireRejectAction: (
    messages: ComponentMessageItem[],
    isDrag: boolean,
    onReject?: (detail: RejectDetail) => void
  ) => any,
  handleFilesAdded: (files: FileList, onCommit?: (detail: CommitDetail) => void) => void,
  rootRef: Ref<HTMLDivElement>,
  translations: BundleType,
  onCommit?: (detail: CommitDetail) => void,
  onReject?: (detail: RejectDetail) => void,
  // setValidity not set for custom filepicker
  setValidity?: StateUpdater<'NA' | 'valid' | 'invalid'>
) => {
  const inDropZone = useRef<boolean>(false);
  const isDroppable = useRef<boolean>(false);
  const dragPromiseResolver = useRef<() => void>();

  /**
   * Drag Enter handler called when a dragged file enter the filepicker dropzone
   */
  const _handleDragEnter = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Drag over handler called when a file is dragged over the filepicker
   */
  const _handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (inDropZone.current) {
      return;
    }

    const browser = getClientHints().browser;
    inDropZone.current = true;
    isDroppable.current = true;
    // Safari doesn't provide the data for the files on drag over so validation cannot be done until drop
    if (browser !== 'safari') {
      if (!event.dataTransfer) {
        return;
      }
      const files = createFileList(event.dataTransfer.items);
      let messages = [] as ComponentMessageItem[];
      const selectionModeValid = validateSelectionMode(files, selectionMode);
      const droppable = validateTypes(files, accept, translations);
      if (selectionModeValid && droppable.rejected.length === 0) {
        // validation passes
        setValidity?.('valid');
      } else {
        isDroppable.current = false;
        if (selectionModeValid) {
          // mimetype Validation fails
          messages = getMimeTypeValidationMessages(droppable.rejected, translations);
        } else {
          // selected multiple files in single selection mode
          messages.push({
            severity: 'error',
            summary: translations.filePicker_singleFileUploadError()
          });
        }
        setValidity?.('invalid');
        dragPromiseResolver.current = fireRejectAction(messages, true, onReject);
      }
    } else {
      setValidity?.('valid');
    }
  };

  /**
   * Drag leave handler is called when the dragged file leaves the filepicker dropzone
   */
  const _handleDragLeave = (event: DragEvent, mimeTypeDropFail = false) => {
    if (!inDropZone.current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!rootRef.current?.contains(event.relatedTarget as Node)) {
      inDropZone.current = false;
      setValidity?.('NA');
      // There's only a promise if validation failed on dragover
      if (!isDroppable.current && !mimeTypeDropFail) {
        dragPromiseResolver.current?.();
      }
    }
  };

  /**
   * Drop file handler is called when a file is dropped on the filepicker dropzone
   */
  const _handleFileDrop = (event: DragEvent) => {
    if (inDropZone.current) {
      event.preventDefault();
      event.stopPropagation();
      if (!event.dataTransfer) {
        inDropZone.current = false;
        return;
      }
      const files = createFileList(event.dataTransfer.files);
      let mimeTypeDropFail = false;
      if (isDroppable.current) {
        let messages: ComponentMessageItem[] = [];
        if (validateSelectionMode(files, selectionMode)) {
          const droppable = validateTypes(files, accept, translations);
          if (droppable.rejected.length > 0) {
            // mimetype Validation fails
            messages = getMimeTypeValidationMessages(droppable.rejected, translations);
            mimeTypeDropFail = true;
          }
        } else {
          messages.push({
            severity: 'error',
            summary: translations.filePicker_singleFileUploadError()
          });
        }
        if (messages.length > 0) {
          isDroppable.current = false;
          fireRejectAction(messages, false, onReject);
        }
        if (isDroppable.current) {
          handleFilesAdded(files, onCommit);
        }
      }
      _handleDragLeave(event, mimeTypeDropFail);
    }
  };

  return {
    onDragEnter: _handleDragEnter,
    onDragOver: _handleDragOver,
    onDragLeave: _handleDragLeave,
    onDrop: _handleFileDrop
  };
};
